"use client";

import * as React from "react";

import { DEFAULT_SORT } from "@/constants";
import { usePagination } from "@/hooks/usePagination";
import type { OrderBy, SearchMatchMode } from "@/types";

interface UseListControlsOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applied: any;
  onResetFilters: () => void;
  onSearchChange: (value: string) => void;
  onMatchModeChange: (value: SearchMatchMode) => void;
}

export function useListControls({
  applied,
  onResetFilters,
  onSearchChange,
  onMatchModeChange,
}: UseListControlsOptions) {
  const [sort, setSort] = React.useState(DEFAULT_SORT);
  const allPagination = usePagination();
  const favoritesPagination = usePagination();

  const { reset: resetAll } = allPagination;
  const { reset: resetFavorites } = favoritesPagination;

  const resetAllPagination = React.useCallback(() => {
    resetAll();
    resetFavorites();
  }, [resetAll, resetFavorites]);

  React.useEffect(() => {
    resetAllPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const handleSort = React.useCallback(() => {
    setSort((prev) => (prev === "asc" ? "desc" : "asc"));
    resetAllPagination();
  }, [resetAllPagination]);

  const handleSetSort = React.useCallback(
    (value: OrderBy) => {
      setSort(value);
      resetAllPagination();
    },
    [resetAllPagination]
  );

  const handleSearchInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(e.target.value);
      resetAllPagination();
    },
    [onSearchChange, resetAllPagination]
  );

  const handleSearchClear = React.useCallback(() => {
    onSearchChange("");
    resetAllPagination();
  }, [onSearchChange, resetAllPagination]);

  const handleMatchModeChange = React.useCallback(
    (value: SearchMatchMode) => {
      onMatchModeChange(value);
      resetAllPagination();
    },
    [onMatchModeChange, resetAllPagination]
  );

  const handleResetAll = React.useCallback(() => {
    onResetFilters();
    setSort(DEFAULT_SORT);
    resetAll();
  }, [onResetFilters, resetAll]);

  return {
    sort,
    allPagination,
    favoritesPagination,
    handleSort,
    handleSetSort,
    handleSearchInputChange,
    handleSearchClear,
    handleMatchModeChange,
    handleResetAll,
  };
}
